/**
 * Groups/Teams Management Page
 *
 * Allows users to create and manage their groups/teams.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface Group {
  id: string;
  name: string;
  description: string | null;
  ownerId: string;
  ownerEmail: string;
  memberCount: number;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
  updatedAt: string;
}

interface GroupMember {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: 'admin' | 'member';
  joinedAt: string;
}

export default function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamsEnabled, setTeamsEnabled] = useState<boolean | null>(null);

  // Create group modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // View members modal
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    checkTeamsEnabled();
  }, []);

  async function checkTeamsEnabled() {
    try {
      const response = await api.get('/api/settings/public');
      if (response.ok) {
        const data = await response.json() as { settings: { teams_enabled?: boolean } };
        setTeamsEnabled(data.settings?.teams_enabled ?? false);
        if (data.settings?.teams_enabled) {
          loadGroups();
        } else {
          setLoading(false);
        }
      } else {
        setTeamsEnabled(false);
        setLoading(false);
      }
    } catch {
      setTeamsEnabled(false);
      setLoading(false);
    }
  }

  async function loadGroups() {
    try {
      setLoading(true);
      const response = await api.get('/api/groups');
      if (response.ok) {
        const data = await response.json() as { groups: Group[] };
        setGroups(data.groups);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load groups');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      const response = await api.post('/api/groups', createForm);
      if (response.ok) {
        setShowCreateModal(false);
        setCreateForm({ name: '', description: '' });
        loadGroups();
      } else {
        const data = await response.json() as { error?: string };
        setCreateError(data.error || 'Failed to create group');
      }
    } catch {
      setCreateError('Failed to connect to server');
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleDeleteGroup(groupId: string) {
    setDeleteLoading(true);
    try {
      const response = await api.delete(`/api/groups/${groupId}`);
      if (response.ok) {
        setDeleteConfirm(null);
        loadGroups();
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to delete group');
      }
    } catch {
      alert('Failed to connect to server');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function loadMembers(group: Group) {
    setSelectedGroup(group);
    setMembersLoading(true);
    try {
      const response = await api.get(`/api/groups/${group.id}/members`);
      if (response.ok) {
        const data = await response.json() as { members: GroupMember[] };
        setMembers(data.members);
      }
    } catch {
      // Ignore errors
    } finally {
      setMembersLoading(false);
    }
  }

  async function handleRemoveMember(memberId: string) {
    if (!selectedGroup) return;
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      const response = await api.delete(`/api/groups/${selectedGroup.id}/members/${memberId}`);
      if (response.ok) {
        setMembers(members.filter(m => m.id !== memberId));
        // Refresh groups to update member count
        loadGroups();
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to remove member');
      }
    } catch {
      alert('Failed to connect to server');
    }
  }

  async function handleUpdateMemberRole(memberId: string, newRole: 'admin' | 'member') {
    if (!selectedGroup) return;

    try {
      const response = await api.patch(`/api/groups/${selectedGroup.id}/members/${memberId}`, {
        role: newRole,
      });
      if (response.ok) {
        setMembers(members.map(m => m.id === memberId ? { ...m, role: newRole } : m));
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to update member role');
      }
    } catch {
      alert('Failed to connect to server');
    }
  }

  // Teams feature is disabled
  if (teamsEnabled === false) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage teams to collaborate with others
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Teams Feature Not Enabled</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            The Teams feature is currently disabled by the administrator.
            Contact your administrator if you need access to team collaboration features.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading || teamsEnabled === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage teams to collaborate with others
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Team
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Groups List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {groups.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams yet</h3>
            <p className="mt-1 text-sm text-gray-500">Create your first team to start collaborating.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Team
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groups.map((group) => (
              <div key={group.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{group.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        group.role === 'owner'
                          ? 'bg-purple-100 text-purple-800'
                          : group.role === 'admin'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {group.role}
                      </span>
                    </div>
                    {group.description && (
                      <p className="mt-1 text-sm text-gray-500 truncate">{group.description}</p>
                    )}
                    <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                      <span>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                      <span>Created {new Date(group.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => loadMembers(group)}
                      className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      View Members
                    </button>
                    {(group.role === 'owner' || group.role === 'admin') && (
                      <Link
                        to={`/settings/invitations?groupId=${group.id}`}
                        className="px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
                      >
                        Invite
                      </Link>
                    )}
                    {group.role === 'owner' && (
                      <button
                        onClick={() => setDeleteConfirm(group.id)}
                        className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Team</h2>

            {createError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Name *
                </label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Marketing Team"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="What is this team for?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || !createForm.name.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createLoading ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Members Modal */}
      {selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedGroup.name}</h2>
                <p className="text-sm text-gray-500">{selectedGroup.memberCount} member{selectedGroup.memberCount !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {membersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No members yet.</p>
                  <p className="text-sm mt-1">Invite people to join this team.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Show owner first */}
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedGroup.ownerEmail}</p>
                      <p className="text-xs text-purple-600">Owner</p>
                    </div>
                  </div>

                  {/* Members */}
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {member.displayName || member.email}
                        </p>
                        {member.displayName && (
                          <p className="text-xs text-gray-500">{member.email}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          {member.role === 'admin' ? 'Admin' : 'Member'} · Joined {new Date(member.joinedAt).toLocaleDateString()}
                        </p>
                      </div>

                      {(selectedGroup.role === 'owner' || selectedGroup.role === 'admin') && (
                        <div className="flex items-center gap-2">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateMemberRole(member.id, e.target.value as 'admin' | 'member')}
                            className="text-sm border border-gray-300 rounded px-2 py-1"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove member"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(selectedGroup.role === 'owner' || selectedGroup.role === 'admin') && (
              <div className="p-4 border-t border-gray-200">
                <Link
                  to={`/settings/invitations?groupId=${selectedGroup.id}`}
                  className="block w-full px-4 py-2 text-center bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Invite Members
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Delete Team?</h2>
            <p className="text-gray-600 mb-4">
              This will permanently delete the team and remove all members. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGroup(deleteConfirm)}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Team'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
